import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import Layout from './components/Layout';
import AuthenticatedDashboard from './components/AuthenticatedDashboard';
import PostureTriagePage from './components/PostureTriagePage';
import AgentFleetDashboard from './components/AgentFleetDashboard';
import AgentDetailPage from './components/AgentDetailPage';
import ClientDetailPage from './components/ClientDetailPage';
import AuditPage from './components/AuditPage';
import SettingsPage from './components/settings/SettingsPage';
import ReleasesPage from './components/releases/ReleasesPage';
import CloudBackupsPage from './components/CloudBackupsPage';
import ReportsPage from './components/ReportsPage';
import ErrorBoundary from './components/ErrorBoundary';
import SessionExpiredModal from './components/SessionExpiredModal';
import { ToastProvider } from './components/Toast';
import './App.css';

// Development-only component harness for screenshot verification (PR-923b).
const BillingHarness = import.meta.env.DEV ? React.lazy(() => import('./dev/BillingHarness')) : null;

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-nippo">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-anchor-blue mx-auto"></div>
          <p className="mt-4 text-anchor-muted">Initializing ANCHOR Security Platform...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// SettingsPage imported from ./components/settings/SettingsPage

// Wrapper for AgentFleetDashboard to pass required props
const AgentsPage: React.FC = () => {
  const { user } = useAuth();
  return <AgentFleetDashboard tenantId={user?.tenantId || ''} />;
};

// Root redirect component
const RootRedirect: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-nippo">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-anchor-blue mx-auto"></div>
          <p className="mt-4 text-anchor-muted">Initializing ANCHOR Security Platform...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
};

// Main App Content
const AppContent: React.FC = () => {
  const { logout } = useAuth();
  const [sessionExpired, setSessionExpired] = useState(false);
  const handlingRef = useRef(false);

  useEffect(() => {
    const handler = () => setSessionExpired(true);
    window.addEventListener('anchor:auth-expired', handler);
    return () => window.removeEventListener('anchor:auth-expired', handler);
  }, []);

  const handleReLogin = useCallback(async () => {
    if (handlingRef.current) return;
    handlingRef.current = true;
    setSessionExpired(false);
    try {
      await logout();
    } catch { /* logout may fail if session is already dead */ }
    window.location.href = '/login';
  }, [logout]);

  return (
    <>
    {sessionExpired && <SessionExpiredModal onSignIn={handleReLogin} />}
    <Routes>
      {/* Public route */}
      <Route path="/login" element={<LoginPageGuard />} />

      {/* Protected routes inside Layout shell */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<PostureTriagePage />} />
        <Route path="/overview" element={<AuthenticatedDashboard />} />
        <Route path="/agents" element={<AgentsPage />} />
        <Route path="/agents/:agentId" element={<AgentDetailPage />} />
        <Route path="/clients/:clientId" element={<ClientDetailPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/cloud-backups" element={<CloudBackupsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/releases" element={<ReleasesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      {/* Dev-only harness; not registered in production builds */}
      {BillingHarness && (
        <Route path="/dev/billing" element={<React.Suspense fallback={null}><BillingHarness /></React.Suspense>} />
      )}

      {/* MSAL silent-renewal iframe loads this path — render nothing so the
         SPA router doesn't redirect away and destroy the #code fragment */}
      <Route path="/auth/callback" element={null} />

      {/* Root redirect */}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
    </>
  );
};

// Guard: redirect authenticated users away from login
const LoginPageGuard: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-nippo">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-anchor-blue mx-auto"></div>
          <p className="mt-4 text-anchor-muted">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LoginPage />;
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
